import { useId, useState } from 'react';
import { ArrowRight, CaretDown, Storefront, WarningCircle } from '@phosphor-icons/react';
import { shopifyCopy } from '@discovr/features/copy';
import { SHOPIFY_PERMISSIONS } from './shopifyScopes';
import { ShopifyPermissionsList } from './ShopifyPermissionsList';
import { Alert } from '../components/ui/alert';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { REDUCED_MOTION_DIALOG_CLASS } from '../components/ui/modalScrim';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Fallback, not the source of truth: the caller passes the copy module's
// string. It exists so a caller that sets a flag and forgets its message
// cannot produce a blocked button with nothing to read.
const DEFAULT_BRAND_BUSY_MESSAGE = shopifyCopy.claim.claimBrandBusy;

export interface ShopifyClaimConfirmDialogProps {
  open: boolean;
  /** Store the claim link would connect. Absent while the preview is in flight. */
  shopDomain?: string;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  /** Claim in flight. Holds the dialog open and blocks a second submit. */
  pending?: boolean;
  /** Claim failed. The token is still held, so the dialog stays open. */
  claimErrorMessage?: string;
  /** Whether pressing the button again could help. False for a terminal
   *  failure, where "Try again" would fail identically every time. */
  claimRetryable?: boolean;
  /** This brand already holds an active store, so the claim would be refused.
   *  Blocks Connect and says what to do, instead of letting the merchant press
   *  a button that can only 409. */
  brandAlreadyConnected?: boolean;
  /** The caller's OWN bound store, named so they know which one to uninstall from. */
  connectedShopDomain?: string;
  /** Copy for the already-connected case. Optional only so the caller may
   *  override it: the dialog falls back to its own string, because a blocked
   *  Connect with an empty explanation is a dead end. */
  brandAlreadyConnectedMessage?: string;
  /** Display name of the signed-in brand, so the dialog names the destination
   *  account and not just the store. Optional: a brand whose profile has not
   *  loaded falls back to the generic title rather than showing "Connect acme
   *  to undefined?". */
  brandName?: string;
  /** Re-run the preview after it failed. Without this a transient preview
   *  failure strands the token exactly as a failed claim used to. */
  onRetryPreview?: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirms which Shopify store a post-install claim link would connect, before
 * connecting it.
 *
 * The claim link is a bearer credential — whoever opens it binds the store. The
 * app used to claim on page load, so anyone could send a signed-in brand user a
 * link for a store they controlled and have it bound to that user's brand with
 * no visible step. Naming the store and requiring a click turns that into a
 * decision the merchant can refuse.
 *
 * Cancel is the safe default: it is the escape action, and no claim is sent
 * unless Connect is pressed.
 *
 * The store name is the whole safety mechanism — it is the one fact the
 * merchant judges the decision on — so the panel holding it is an aria-live
 * region. Without that, a screen-reader user keeps focus on Cancel and is never
 * told which store they are approving.
 */
export function ShopifyClaimConfirmDialog({
  open,
  shopDomain,
  isLoading,
  isError,
  errorMessage,
  pending = false,
  claimErrorMessage,
  claimRetryable = false,
  brandAlreadyConnected = false,
  connectedShopDomain,
  brandAlreadyConnectedMessage = DEFAULT_BRAND_BUSY_MESSAGE,
  brandName,
  onRetryPreview,
  onConfirm,
  onCancel,
}: ShopifyClaimConfirmDialogProps) {
  // Radix holds content mounted for a 200ms exit animation, by which point the
  // parent has dropped the token and shopDomain is undefined. Rendering the
  // error panel there announced "we couldn't check that link" through
  // role="alert" right after a SUCCESSFUL connect; rendering the spinner
  // announced "Checking the store…" instead, which is no better. While closing,
  // say nothing and let nothing be pressed. An earlier version cached the last
  // resolved domain, which could show store A's name while connecting store B.
  const unresolved = open && (isError || (!isLoading && !shopDomain));
  const settling = !open || (!unresolved && !shopDomain && !isLoading);
  const terminalClaimError = !!claimErrorMessage && !claimRetryable;
  // A brand that already holds a store cannot bind another: Claim refuses it,
  // so offering Connect here would only produce a 409 the merchant has to
  // decode. Block it in the dialog and say what to do instead.
  const blocked =
    isLoading || unresolved || terminalClaimError || settling || brandAlreadyConnected;

  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const permissionsPanelId = useId();
  // Both halves or neither: a title naming one side and not the other reads
  // worse than the generic question it replaces.
  const shopName = shopDomain?.replace(/\.myshopify\.com$/, '');
  const named = shopName && brandName ? { shopName, brandName } : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onCancel();
      }}
    >
      <DialogContent
        data-clarity-mask="True"
        // max-w-lg, matching the other connect-flow dialogs
        // (BrandConnectWizard, ConnectWizard, ShopifyPermissionsDialog). At md
        // the store → brand row had ~190px for a .myshopify.com domain.
        //
        // The height cap is the same one ShopifyPermissionsDialog uses, and it
        // is load-bearing here: DialogContent is `fixed` and centred with no
        // max-height, so at 320px/400% zoom this dialog measured 486px in a
        // 256px viewport and was clipped at BOTH ends — the Connect button sat
        // at y=427, and a fixed element cannot be scrolled to. Capped, the
        // dialog becomes its own scroll container (484 > 222), so focus can
        // reach the button. This is the confirmation step that stops a store
        // being bound from a link someone sent, so an unreachable button is
        // the worst possible failure here.
        className={`max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg ${REDUCED_MOTION_DIALOG_CLASS}`}
        // The token is single-use and already gone from the URL, so an
        // accidental dismissal mid-claim would strand the merchant.
        onEscapeKeyDown={(e) => pending && e.preventDefault()}
        onInteractOutside={(e) => pending && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {named
              ? `Connect ${named.shopName} to ${named.brandName}?`
              : 'Connect this Shopify store?'}
          </DialogTitle>
          <DialogDescription>
            Discovr will create discount codes in this store and credit its orders to your creators.
          </DialogDescription>
        </DialogHeader>

        {unresolved ? (
          <Alert className="text-muted-foreground">
            <WarningCircle aria-hidden="true" />
            <span>{errorMessage}</span>
          </Alert>
        ) : (
          <div role="status" aria-live="polite">
            {shopDomain ? (
              /* One row on desktop, stacked below sm. Both halves on one
                 line in a 375px dialog left each a fraction of the width, so
                 the domain broke mid-word and the brand name was cut to
                 "Test De…" — and these two names are the whole safety
                 mechanism this dialog exists for. */
              <div className="border-border flex flex-col gap-2 rounded-md border px-3 py-3 sm:flex-row sm:items-center">
                <div className="flex min-w-0 items-center gap-2">
                  <Storefront
                    className="text-muted-foreground size-5 shrink-0"
                    aria-hidden="true"
                  />
                  {/* wrap-break-word, not break-all. Neither breaks at the
                      dots — CSS allows no break between "." and a letter — but
                      measured in Chromium at 120px, break-all splits the
                      readable label itself ("discovr-test.mys" / "hopify.com")
                      while this breaks after the hyphen first
                      ("discovr-" / "test.myshopify.co" / "m"), splitting
                      mid-word only once nothing else fits. Combined with the
                      row stacking below sm, the domain fits on one line at
                      375px. */}
                  <span className="text-foreground min-w-0 font-medium wrap-break-word">
                    {shopDomain}
                  </span>
                </div>
                {brandName ? (
                  <>
                    <ArrowRight
                      className="text-muted-foreground size-4 shrink-0 rotate-90 sm:rotate-0"
                      aria-hidden="true"
                    />
                    <div className="flex min-w-0 items-center gap-2">
                      {/* The fallback initial is decorative: role="status"
                          is aria-atomic, so without this the region announced
                          "discovr-test.myshopify.com T Test Demo Brand". */}
                      <Avatar size="sm" className="shrink-0" aria-hidden="true">
                        <AvatarFallback>{brandName.trim().charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      {/* Never truncated. The destination account is half of
                          what the merchant is approving, and "Test De…" could
                          be any of several brands they belong to. It wraps
                          rather than clipping, so the title is only a
                          convenience for reading a long name at a glance — it
                          is not what makes the name available. */}
                      <span
                        className="text-foreground min-w-0 font-medium wrap-break-word"
                        title={brandName}
                      >
                        {brandName}
                      </span>
                    </div>
                  </>
                ) : null}
              </div>
            ) : settling ? null : (
              <div className="text-muted-foreground flex items-center gap-2 px-1 py-2 text-body-sm-regular">
                <LoadingSpinner /> Checking the store…
              </div>
            )}

            {/* Inside the existing polite region rather than beside it. A
                second live region competes with this one, and an assertive
                role="alert" would pre-empt the store name outright — which the
                comment above calls the whole safety mechanism. Nothing here is
                an error either: the merchant has not acted yet. */}
            {shopDomain && brandAlreadyConnected ? (
              <Alert variant="warning" role={undefined} className="mt-2">
                <WarningCircle aria-hidden="true" />
                <span>
                  {brandAlreadyConnectedMessage}
                  {connectedShopDomain ? ` Connected store: ${connectedShopDomain}.` : null}
                </span>
              </Alert>
            ) : null}
          </div>
        )}

        {/* The point of commitment used to disclose nothing — the permissions
            were shown on the card BEFORE connecting and nowhere here. Collapsed
            by default: this is reference material, and expanded it outranked
            the sentence the merchant is actually deciding on.
            Not "will ask for": the merchant granted these during the Shopify
            install, and the claim requests nothing. */}
        {shopDomain && !unresolved && !brandAlreadyConnected ? (
          <div className="px-1">
            <button
              type="button"
              aria-expanded={permissionsOpen}
              aria-controls={permissionsOpen ? permissionsPanelId : undefined}
              className="text-muted-foreground hover:text-foreground focus-visible:outline-ring flex items-center gap-1 rounded-sm text-caption-regular focus-visible:outline-2"
              onClick={() => setPermissionsOpen((o) => !o)}
            >
              <CaretDown
                className={cn('size-3 transition-transform', permissionsOpen && 'rotate-180')}
                aria-hidden="true"
              />
              {`Permissions granted (${SHOPIFY_PERMISSIONS.length})`}
            </button>
            {/* Unmounted rather than hidden while collapsed: six rows of
                label-and-purpose left in the tree are six more stops a screen
                reader walks past before reaching Connect. */}
            {permissionsOpen ? (
              <div id={permissionsPanelId} className="mt-1.5">
                <ShopifyPermissionsList rows={SHOPIFY_PERMISSIONS} />
              </div>
            ) : null}
          </div>
        ) : null}

        {claimErrorMessage && !unresolved ? (
          <div role="alert" className="text-destructive px-1 text-body-sm-regular">
            {claimErrorMessage}
          </div>
        ) : null}

        {/* Moved out of the description: as the last sentence there it read as
            part of what Discovr does, rather than as the one check that stops
            a merchant binding a store they were sent a link for. */}
        {shopDomain && !unresolved && !brandAlreadyConnected ? (
          <p className="text-muted-foreground px-1 text-caption-regular">
            Didn’t just install Discovr on this store? Cancel.
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="sm:flex-1" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          {unresolved && onRetryPreview ? (
            <Button className="sm:flex-1" onClick={onRetryPreview}>
              Retry
            </Button>
          ) : (
            <Button
              className="sm:flex-1"
              disabled={blocked}
              // aria-disabled, not disabled, while pending: `disabled` drops
              // focus off the button the keyboard user just pressed, and it
              // never comes back when "Try again" appears.
              aria-disabled={pending || undefined}
              onClick={() => {
                if (!pending && !blocked) onConfirm();
              }}
            >
              {pending ? 'Connecting…' : claimRetryable ? 'Try again' : 'Connect store'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
